/**
 * InstallPWAPrompt
 * -------------------------------------------------------------------
 * Banner que orienta o usuário a instalar a aplicação como PWA.
 *  - iOS: mostra instruções visuais (Compartilhar → Adicionar à Tela de Início)
 *  - Android: aproveita o evento `beforeinstallprompt` para disparar instalação
 *
 * Dismissível via localStorage para não incomodar.
 */

import { useCallback, useEffect, useState } from "react";
import { Bell, Share, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const DISMISS_KEY = "bravaforte:pwa_install_dismissed_at";
// Reexibe o banner após 7 dias, caso o usuário tenha descartado
const DISMISS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

interface InstallPWAPromptProps {
  platform: "ios" | "android";
}

function isRecentlyDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_WINDOW_MS;
  } catch {
    return false;
  }
}

export const InstallPWAPrompt = ({ platform }: InstallPWAPromptProps) => {
  const [dismissed, setDismissed] = useState<boolean>(() => isRecentlyDismissed());
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  // Captura o evento do Chrome/Android antes do prompt nativo
  useEffect(() => {
    if (platform !== "android") return;
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [platform]);

  const handleDismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // storage indisponível — ignora
    }
    setDismissed(true);
  }, []);

  const handleAndroidInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === "accepted") {
      setDeferredPrompt(null);
    }
  }, [deferredPrompt]);

  if (dismissed) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-4 pointer-events-none">
      <Card className="pointer-events-auto w-full max-w-md border border-border bg-background p-4 shadow-lg">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Bell className="h-4 w-4" aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">
              Receber notificações de diligências
            </p>

            {platform === "ios" ? (
              <div className="mt-2 space-y-2 text-xs text-muted-foreground">
                <p>
                  Para ativar notificações no iPhone, adicione o BravaForte à tela de início:
                </p>
                <ol className="list-decimal space-y-1 pl-4">
                  <li className="flex items-center gap-1.5">
                    <span>Toque em Compartilhar</span>
                    <Share className="h-3 w-3 inline" aria-hidden="true" />
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span>Escolha "Adicionar à Tela de Início"</span>
                    <Plus className="h-3 w-3 inline" aria-hidden="true" />
                  </li>
                  <li>Abra pelo ícone instalado para ativar as notificações</li>
                </ol>
              </div>
            ) : (
              <p className="mt-1 text-xs text-muted-foreground">
                Instale o BravaForte como aplicativo para receber mensagens do chat e
                atualizações de diligência direto no seu celular.
              </p>
            )}

            {platform === "android" && deferredPrompt ? (
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="primary" onClick={handleAndroidInstall}>
                  Instalar aplicativo
                </Button>
                <Button size="sm" variant="ghost" onClick={handleDismiss}>
                  Agora não
                </Button>
              </div>
            ) : (
              <div className="mt-3">
                <Button size="sm" variant="ghost" onClick={handleDismiss}>
                  Entendi, fechar
                </Button>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Fechar aviso"
            className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </Card>
    </div>
  );
};
